import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Typography } from '@mui/material';
import { SCUIButton } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';

import SummaryAggregationQuery from '../../../../services/query/summaryAggregation/SummaryAggregationQuery.services';
import TimeHelper from '../../../../utils/time';

import { SL } from '../summaryList.classes';

interface SummaryListDetailProps {
    summary: any;
}

const prepareSummaryItems = (items: any[]): any[] => {
    if (!items?.length) return [];

    return items
        .filter((item) => item?.summaryItemDefinition?.visible !== false)
        .slice()
        .sort((a, b) => {
            const aOrder = String(a?.summaryItemDefinition?.displayOrder ?? '');
            const bOrder = String(b?.summaryItemDefinition?.displayOrder ?? '');
            return aOrder.localeCompare(bOrder, undefined, { numeric: true });
        })
        .map((item) => ({
            ...item,
            id: item?.id,
        }));
};

const formatAmount = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '-';
    }
    return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const SummaryListDetail = ({ summary }: SummaryListDetailProps) => {
    const { t } = useTranslation();
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const detailRows = useMemo(
        () => prepareSummaryItems(summary?.summaryItems || []),
        [summary?.summaryItems]
    );

    const periodBegin = summary?.periodBeginDate
        ? TimeHelper.formatDateWithDot(summary.periodBeginDate)
        : '-';
    const periodEnd = summary?.periodEndDate
        ? TimeHelper.formatDateWithDot(summary.periodEndDate)
        : periodBegin;

    const handleDownload = (summaryItemId: number) => {
        setDownloadingId(summaryItemId);
        SummaryAggregationQuery.ExportSummaryDetails({ summaryItemId })
            .then((res: Blob) => {
                const url = window.URL.createObjectURL(new Blob([res]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `summary-detail-${summaryItemId}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setDownloadingId(null);
            });
    };

    const columns = [
        {
            field: 'id',
            headerName: t('summaryAggregation.summaryList.detailColumns.id'),
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'amount',
            headerName: t('summaryAggregation.summaryList.detailColumns.amount'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => formatAmount(values?.row?.amount),
        },
        {
            field: 'computedAmount',
            headerName: t('summaryAggregation.summaryList.detailColumns.calculatedAmount'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => {
                const amount =
                    values?.row?.computedAmount ??
                    values?.row?.calculatedAmount ??
                    values?.row?.amount;
                return formatAmount(amount);
            },
        },
        {
            field: 'description',
            headerName: t('summaryAggregation.summaryList.detailColumns.description'),
            flex: 1.5,
            align: 'left',
            headerAlign: 'center',
            renderCell: (values: any) =>
                values?.row?.summaryItemDefinition?.description ?? values?.row?.description ?? '-',
        },
        {
            field: 'keyword',
            headerName: t('summaryAggregation.summaryList.detailColumns.keyword'),
            flex: 1.5,
            align: 'left',
            headerAlign: 'center',
            renderCell: (values: any) => {
                const definition = values?.row?.summaryItemDefinition ?? {};
                const keyword =
                    values?.row?.itemDefinitionKeyword ??
                    definition?.itemDefinitionKeyword ??
                    definition?.keyword ??
                    definition?.key;
                return keyword ? String(keyword).trim() : '-';
            },
        },
        {
            field: 'download',
            headerName: t('summaryAggregation.summaryList.detailColumns.downloadDetail'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => (
                <SCUIButton
                    text={t('summaryAggregation.summaryList.downloadDetail')}
                    type="secondary"
                    className={SL.btnDownloadDetail}
                    sizeType="fixed"
                    buttonType="button"
                    size={'m'}
                    disabled={downloadingId === values?.row?.id}
                    onClick={() => handleDownload(values?.row?.id)}
                    showStartIcon
                    startIcon={<FileDownloadIcon fontSize="small" />}
                />
            ),
        },
    ];

    if (!summary?.id) {
        return null;
    }

    return (
        <Box className={SL.detail} sx={{ marginTop: 4 }}>
            <Typography className={SL.detailDateLabel} sx={{ fontWeight: 600, mb: 1 }}>
                {t('summaryAggregation.summaryList.detailDateLabel', {
                    begin: periodBegin,
                    end: periodEnd,
                })}
            </Typography>
            <Typography className={SL.detailItemsTitle} sx={{ fontWeight: 600, mb: 2 }}>
                {t('summaryAggregation.summaryList.itemsTitle')}
            </Typography>
            <Box
                className={SL.detailGrid}
                sx={{
                    [`& .${SL.detailRowColored}`]: {
                        fontWeight: 700,
                    },
                    [`& .${SL.detailRowColored} .MuiDataGrid-cell`]: {
                        fontWeight: 700,
                    },
                }}
            >
                <SCUICustomDataGrid
                    getRowClassName={(params: any) =>
                        params?.row?.summaryItemDefinition?.coloredRow === true
                            ? `${SL.detailRowColored} sa-summary-list-detail-grid`
                            : 'sa-summary-list-detail-grid'
                    }
                    rows={detailRows}
                    columns={columns}
                    rowHeight={75}
                />
            </Box>
        </Box>
    );
};
